/* Copyright (c) 2020-present CloudIO Inc */

import { Page, AnyItem, ItemType } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionOnConfirm';
import { getManager } from '../../registry/Manager';

const ActionOnConfirmManager: ActionManagerFn<'ActionOnConfirm'> = (
  platform: Platform,
): ActionManager<'ActionOnConfirm'> => {
  return {
    ...baseActionManager(platform, 'ActionOnConfirm'),
    isChildAllowed: (page, item, childType) =>
      getManager(childType).managerType === 'Action',
    defaultValue,
    props: {
      ...baseActionProps,
    },
    name: 'On Confirm',
    icon: { iconName: 'check' },
    description: `**Overview**<br/>
The "On Confirm" action is a specialized logic container designed solely to handle the "positive" outcome of a User Confirmation dialog. It acts as the "Yes" or "OK" branch for the confirmation workflow, executing a defined sequence of child actions only when the user accepts the prompt in the confirmation pop-up.<br/><br/>
Like its counterpart "On Cancel," this component is structurally dependent on the ActionUserConfirmation component. It serves as the wrapper for the core logic that the user is intending to perform (e.g., the actual delete or save operation) once they have verified their intent.

**Key Features**<br/>
* **Strict Parent Scope:** Explicitly restricted via isAllowedInsideParent to function only as a child of ActionUserConfirmation. It serves as the primary execution path for that parent component.
* **Logic Grouping:** Functions as a generic container that allows any standard Action component to be added as a child (isChildAllowed).
* **Event Handling:** Automatically triggered by the "Confirm," "Yes," or "OK" event of the parent confirmation window.

**Properties**<br/>
* **Base Properties:** This component utilizes ...baseActionProps exclusively, accepting only the standard configuration options available to all actions.

**Use Cases**<br/>
* **Critical Operations:** Containing the ActionDeleteRecord logic so that it only executes after the user clicks "Yes" on the "Are you sure?" dialog.
* **Workflow Submission:** Triggering an ActionInvokeCloudFunction to process an order only after the user confirms the total amount.`,
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return (['ActionUserConfirmation'] as ItemType[]).includes(parent.type);
    },
  };
};

export default ActionOnConfirmManager;
